/**
 * Lyric Generator
 *
 * Client-side service for AI-powered lyric generation using the
 * generate-song-content edge function.
 */

import { SUPABASE_URL } from '../../config/config.js';
import { authService } from '../../auth/auth-service.js';

/**
 * Lyric generation options
 * @typedef {Object} LyricOptions
 * @property {string} theme - Main theme or subject of the lyrics
 * @property {string} [mood] - Emotional mood (e.g., "melancholic", "uplifting")
 * @property {string} [genre] - Genre context for lyrical style
 * @property {string} [style] - Lyrical style (e.g., "poetic", "conversational")
 * @property {'verse-chorus' | 'freeform' | 'spoken-word'} [structure] - Song structure
 */

/**
 * Lyric generation result
 * @typedef {Object} LyricResult
 * @property {string} lyrics - The generated lyrics text
 * @property {Object} metadata - Generation metadata
 */

/**
 * Generate lyrics using AI
 * @param {LyricOptions} options - Generation options
 * @returns {Promise<LyricResult>}
 */
export async function generateLyrics(options) {
  const { theme, mood, genre, style, structure } = options;

  if (!theme || theme.trim().length < 3) {
    throw new Error('Theme is required (minimum 3 characters)');
  }

  // Get auth token
  const session = await authService.getSession();
  if (!session?.access_token) {
    throw new Error('Authentication required for lyric generation');
  }

  const response = await fetch(`${SUPABASE_URL}/functions/v1/generate-song-content`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`
    },
    body: JSON.stringify({
      type: 'lyrics',
      theme: theme.trim(),
      mood: mood || undefined,
      genre: genre || undefined,
      style: style || undefined,
      structure: structure || undefined
    })
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Generation failed: ${response.status}`);
  }

  const data = await response.json();

  return {
    lyrics: data.content,
    metadata: data.metadata || {}
  };
}

/**
 * Mood suggestions for lyrics
 */
export const LYRIC_MOODS = [
  'Melancholic',
  'Uplifting',
  'Nostalgic',
  'Romantic',
  'Rebellious',
  'Peaceful',
  'Mysterious',
  'Joyful',
  'Heartbroken',
  'Empowering'
];

/**
 * Style suggestions for lyrics
 */
export const LYRIC_STYLES = [
  'Poetic',
  'Conversational',
  'Abstract',
  'Storytelling',
  'Metaphorical',
  'Direct',
  'Stream of consciousness'
];

/**
 * Structure options
 */
export const LYRIC_STRUCTURES = [
  { value: 'verse-chorus', label: 'Verse-Chorus' },
  { value: 'freeform', label: 'Freeform' },
  { value: 'spoken-word', label: 'Spoken Word' }
];

export default { generateLyrics, LYRIC_MOODS, LYRIC_STYLES, LYRIC_STRUCTURES };
